import type { ComponentProps, ReactElement } from 'react'
import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'

import GeoContext from 'contexts/geoContext'
import { usePlaceByCoords } from 'hooks/usePlace'
import { getPlaceByRegionCode } from 'constants/places'
import type OfferCardCarouselSection from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'
import { OFFER_LIST_CAROUSEL_DEFAULT_LIMIT } from 'components/OfferList/offerListCarouselConstants'
import { getSearchQueryFromFilter } from 'lib/search/searchUtils'
import { getLuxPlusExperienceSearchPlace } from 'lib/experiences/experienceUtils'
import { getCanViewLuxPlusBenefits, getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import { useAppSelector } from 'hooks/reduxHooks'

interface Props {
  place?: App.Place
  title?: string | ReactElement
  subtitle?: string
  carouselId?: string
  campaigns?: Array<string>
}

export function useExperienceLuxPlusCarouselProps(props: Props = {}) {
  const {
    place,
    title,
    subtitle,
    carouselId,
    campaigns,
  } = props

  const intl = useIntl()
  const isLuxPlusExperiencesEnabled = useAppSelector(getIsEnabledLuxPlusDiscountedExperiences)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)

  const { currentRegionCode, latitude, longitude } = useContext(GeoContext)
  const [userPlace] = usePlaceByCoords(latitude, longitude)

  const luxPlusCarouselProps = useMemo<ComponentProps<typeof OfferCardCarouselSection> | undefined>(() => {
    if (!isLuxPlusExperiencesEnabled) return

    const currentPlace = getPlaceByRegionCode(currentRegionCode)
    const isMember = canViewLuxPlusBenefits
    const finalPlace = getLuxPlusExperienceSearchPlace(place ?? userPlace ?? currentPlace)

    // OfferCardCarouselSection uses limit={8} with tabletLimit="default" on tablet+.
    const discountedExperiencesFilter: App.OfferListFilters = {
      offerTypes: ['experience'],
      placeIds: [finalPlace.id],
      destinationId: finalPlace.id,
      destinationName: finalPlace.name,
      luxPlusFeatures: ['Member prices'],
      limit: OFFER_LIST_CAROUSEL_DEFAULT_LIMIT,
      campaigns,
    }

    return {
      tileSize: 'medium',
      title: title ?? (isMember
        ? intl.formatMessage({
            id: 'eBsbQj-z8G-Llnw9c42C',
            defaultMessage: 'Local discounts exclusive to you',
            description: 'Carousel title for LuxPlus member showing exclusive local experience discounts',
          })
        : intl.formatMessage({
            id: 't38L600mSmQ3gymyHe6p',
            defaultMessage: 'Local discounts exclusive to LuxPlus+ members',
            description: 'Carousel title for non-member promoting LuxPlus exclusive experience discounts',
          })),
      subtitle: subtitle ?? intl.formatMessage({
        id: '9NYTGS3m0OP7olfNsZQy',
        defaultMessage: 'Enjoy discounted experiences without the need for travelling',
        description: 'Carousel subtitle for LuxPlus discounted experiences section',
      }),
      filters: {
        ...discountedExperiencesFilter,
      },
      allLink: `/search/experiences?${getSearchQueryFromFilter(discountedExperiencesFilter)}`,
      id: carouselId ?? 'discounted_experiences',
      hideWhenEmpty: true,
    }
  }, [intl, isLuxPlusExperiencesEnabled, currentRegionCode, canViewLuxPlusBenefits, place, userPlace, title, subtitle, carouselId, campaigns])

  return luxPlusCarouselProps
}
